"use client";

import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useState } from "react";
export default function ThePlanningProcess() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: timelineRef,
    isVisible: timelineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: progressRef,
    isVisible: progressVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const [progressValue, setProgressValue] = useState(0);
  useEffect(() => {
    if (progressVisible) {
      const timer = setTimeout(() => setProgressValue(70), 200);
      return () => clearTimeout(timer);
    }
  }, [progressVisible]);
  const steps = [{
    number: "01",
    title: "Initial Consultation",
    description: "We analyze your current asset landscape and discuss your long-term legacy objectives.",
    duration: "45 minutes"
  }, {
    number: "02",
    title: "Jurisdictional Review",
    description: "Our attorneys evaluate your current legal standing and jurisdictional requirements.",
    duration: "5 days"
  }, {
    number: "03",
    title: "Structural Design",
    description: "We draft your customized trust instruments and asset protection frameworks.",
    duration: "2 weeks"
  }, {
    number: "04",
    title: "Execution & Filing",
    description: "Formalizing your legal structures and ensuring all filings are meticulously recorded.",
    duration: "10 days"
  }, {
    number: "05",
    title: "Legacy Stewardship",
    description: "Ongoing advisory, annual reviews, and proactive adjustments to your legal architecture.",
    duration: "Ongoing"
  }];
  return <section data-section-id="270" id="the-planning-process" className="relative overflow-hidden py-32 bg-background text-foreground">
      <div className="container mx-auto px-6 lg:px-8 max-w-7xl">
        {}
        <div ref={headerRef} className={`max-w-3xl mb-20 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-xs font-mono uppercase tracking-[0.2em] mb-6 text-[hsl(25_60%_45%)]">
            — The Architecture of Autonomy
          </p>
          <h2 className="font-default font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6 text-foreground">
            From initial inquiry to{" "}
            <em className="italic text-[hsl(25_60%_45%)]">
              secure legacy.
            </em>
          </h2>
          <p className="font-default text-base lg:text-lg text-secondary leading-relaxed max-w-2xl">
            A precise, analytical journey designed for those seeking sovereignty. No ambiguity, no oversight — just a structured, professional path to securing your financial future.
          </p>
        </div>

        {}
        <div ref={timelineRef} className="relative">
          {}
          <div className="hidden lg:block absolute top-[50%] left-0 right-0 h-px bg-border" />

          {}
          <div className="lg:hidden absolute top-0 bottom-0 left-6 w-px bg-border" />

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-4 relative">
            {steps.map((step, i) => <div key={i} data-index={i} className={`relative transition-all duration-700 ease-out ${timelineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                {}
                <div className="hidden lg:flex absolute top-[calc(50%-6px)] left-1/2 -translate-x-1/2 z-10">
                  <span className="block w-3 h-3 bg-[hsl(25_60%_45%)]" />
                </div>

                {}
                <div className="lg:hidden absolute top-6 left-6 -translate-x-1/2 z-10">
                  <span className="block w-3 h-3 bg-[hsl(25_60%_45%)]" />
                </div>

                <Card className="lg:mb-12 ml-14 lg:ml-0 p-6 border-t border-x-0 border-b-0 border-border bg-card hover:bg-muted transition-colors duration-300 gap-0 shadow-md">
                  <div className="font-default font-serif text-5xl mb-4 leading-none text-[hsl(25_60%_45%)]">
                    {step.number}
                  </div>
                  <h3 className="font-default font-serif text-xl mb-3 text-foreground leading-snug">
                    {step.title}
                  </h3>
                  <p className="font-default text-sm text-secondary leading-relaxed mb-5 line-clamp-2">
                    {step.description}
                  </p>
                  <div className="pt-4 border-t border-border">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(25_60%_45%)]">
                      Duration: {step.duration}
                    </span>
                  </div>
                </Card>
              </div>)}
          </div>
        </div>

        {}
        <div ref={progressRef} className={`mt-24 max-w-2xl mx-auto transition-all duration-1000 ease-out ${progressVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[hsl(25_60%_45%)]">
              Average Engagement Timeline
            </span>
            <span className="font-default font-serif text-2xl text-foreground">30 days</span>
          </div>
          <Progress value={progressValue} className="h-1 bg-border [&>[data-slot=progress-indicator]]:bg-[hsl(25_60%_45%)] transition-all duration-[1500ms] ease-out" />
          <p className="font-default text-xs text-secondary mt-4 text-center">
            From initial consultation to final execution — our firm ensures a rigorous and timely process for every client.
          </p>
        </div>
      </div>
    </section>;
}